/**
 * ユーザー行動トラッキング
 *
 * 収集イベント:
 *   - page_view: ページ遷移
 *   - search: 検索クエリ実行
 *   - click: ボタン・リンククリック
 *   - review_view: レビュー閲覧（PV計測）
 *   - scroll_depth: スクロール深度（25/50/75/100%）
 *   - session_start / session_end: セッション開始・終了
 *
 * プライバシー:
 *   - PII は送信しない（ユーザー名・メールアドレス等は含まない）
 *   - セッション ID はランダム生成（Cookie 不使用）
 *   - IP はサーバー側でハッシュ化
 */

import { apiPost, getApiUrl } from './api';

const SESSION_KEY = 'analytics_session';
const FLUSH_INTERVAL_MS = 15000;
const SESSION_IDLE_MS = 30 * 60 * 1000;
const MAX_BUFFER_SIZE = 50;
const SENSITIVE_QUERY_KEYS = new Set(['token', 'auth_token', 'access_token', 'refresh_token', 'code', 'state']);

let _sessionId = null;
let _lastActivity = 0;
let _sessionRotated = false;
let _buffer = [];
// eslint-disable-next-line no-unused-vars
let _flushTimer = null;
let _sessionStart = null;
let _scrollDepthsSent = new Set();
let _currentPage = null;
let _shortPageTimer = null;
let _scrollEvalTimer = null;
let _initialized = false;

function _newSessionId() {
  return 'ses_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _persistSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id: _sessionId, ts: _lastActivity }));
  } catch (_) { /* ignore */ }
}

// 活動のたびに有効期限を延長（スライディング30分）。30分無操作なら新しいセッションに切り替える。
function _getSessionId() {
  const now = Date.now();
  if (!_sessionId) {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.id && now - parsed.ts < SESSION_IDLE_MS) {
          _sessionId = parsed.id;
          _lastActivity = now;
          _persistSession();
          return _sessionId;
        }
      }
    } catch (_) { /* ignore */ }
    _sessionId = _newSessionId();
  } else if (now - _lastActivity >= SESSION_IDLE_MS) {
    _sessionId = _newSessionId();
    _sessionStart = now;
    _scrollDepthsSent = new Set();
    _sessionRotated = true;
  }
  _lastActivity = now;
  _persistSession();
  return _sessionId;
}

function _safePage(value) {
  try {
    const url = new URL(value, window.location.origin);
    for (const key of SENSITIVE_QUERY_KEYS) {
      url.searchParams.delete(key);
    }
    return `${url.pathname}${url.search}`;
  } catch (_) {
    return String(value || '').split('#')[0].slice(0, 240);
  }
}

function _safeCurrentUrl() {
  try {
    const url = new URL(window.location.href);
    for (const key of SENSITIVE_QUERY_KEYS) {
      url.searchParams.delete(key);
    }
    url.hash = '';
    return url.toString();
  } catch (_) {
    return window.location.pathname;
  }
}

function _sendBeacon(events) {
  const payload = JSON.stringify({
    session_id: _getSessionId(),
    events,
  });
  const url = getApiUrl('/api/analytics/events');
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return true;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: payload,
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}

export function detectDevice(ua = navigator.userAgent || '', maxTouchPoints = navigator.maxTouchPoints || 0) {
  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return 'tablet';
  // iPadOS 13+ は Macintosh を名乗るのでタッチ点数で判別
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'tablet';
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'mobile' : 'tablet';
  if (/iPhone|iPod|Mobile|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

// エラーメッセージを短いコードに正規化（URL・メールアドレスなど PII になり得るものを除去）
export function normalizeErrorMessage(message) {
  return String(message == null ? '' : message)
    .replace(/https?:\/\/\S+/gi, 'url')
    .replace(/[^\s@]+@[^\s@]+/g, 'email')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}

function _enqueue(event) {
  const prevSessionId = _sessionId;
  event.session_id = _getSessionId();
  if (_sessionRotated) {
    _sessionRotated = false;
    if (event.type !== 'session_start' && prevSessionId) {
      _buffer.push({
        type: 'session_start',
        session_id: event.session_id,
        ts: new Date().toISOString(),
        url: _safeCurrentUrl(),
        screen: `${window.innerWidth}x${window.innerHeight}`,
        device: detectDevice(),
        referrer: '',
        language: navigator.language || '',
      });
    }
  }
  event.ts = new Date().toISOString();
  event.url = _safeCurrentUrl();
  event.screen = `${window.innerWidth}x${window.innerHeight}`;
  event.device = detectDevice();
  if (event.type === 'action_error' && 'message' in event) {
    event.message = normalizeErrorMessage(event.message);
  }

  _buffer.push(event);

  if (_buffer.length >= MAX_BUFFER_SIZE) {
    _flush();
  }
}

async function _flush(options = {}) {
  if (_buffer.length === 0) return;

  const events = [..._buffer];
  _buffer = [];

  if (options.beacon && _sendBeacon(events)) {
    return;
  }

  try {
    await apiPost('/api/analytics/events', {
      session_id: _getSessionId(),
      events,
    });
  } catch (_) {
    // 送信失敗時はバッファに戻さない（ログの欠損は許容）
  }
}

// ── 公開 API ──────────────────────────────────────

export function initAnalytics() {
  if (_initialized) return;
  _initialized = true;

  _sessionStart = Date.now();

  _enqueue({
    type: 'session_start',
    referrer: document.referrer || '',
    language: navigator.language || '',
  });

  _flushTimer = setInterval(_flush, FLUSH_INTERVAL_MS);

  const sendSessionEnd = () => {
    // 放置されたタブ（30分以上無操作）で滞在時間が数時間に膨らまないよう、最後の操作時刻で打ち切る
    const now = Date.now();
    const end = _lastActivity && now - _lastActivity >= SESSION_IDLE_MS ? _lastActivity : now;
    const durationSec = Math.max(0, Math.round((end - _sessionStart) / 1000));
    _enqueue({
      type: 'session_end',
      duration_sec: durationSec,
    });
    _flush({ beacon: true });
  };

  // モバイルでは beforeunload が発火しないため、非表示化・pagehide でも送る（集計側は最大値で重複排除）
  window.addEventListener('beforeunload', sendSessionEnd);
  window.addEventListener('pagehide', sendSessionEnd);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      sendSessionEnd();
    }
  });

  _currentPage = window.location.pathname;
  _trackScrollDepth();
  _trackClicks();
}

export function trackPageView(page, title) {
  _scrollDepthsSent = new Set();
  const safePage = _safePage(page);
  _currentPage = safePage.split('?')[0] || window.location.pathname;
  _scheduleScrollEval();
  const utm = {};
  try {
    const qs = safePage.includes('?') ? safePage.slice(safePage.indexOf('?')) : '';
    const params = new URLSearchParams(qs);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
      const val = params.get(key);
      if (val) utm[key] = val;
    });
  } catch (_) {
    /* ignore */
  }
  _enqueue({
    type: 'page_view',
    page: safePage,
    title: title || document.title,
    referrer: document.referrer || '',
    ...utm,
  });
}

export function trackSearch(query, filters, resultsCount) {
  _enqueue({
    type: 'search',
    query,
    filters,
    results_count: resultsCount,
  });
}

export function trackClick(target, metadata) {
  _enqueue({
    type: 'click',
    target,
    ...(metadata || {}),
  });
}

export function trackReviewView(reviewId) {
  _enqueue({
    type: 'review_view',
    review_id: reviewId,
  });
}

export function trackEvent(eventType, data) {
  _enqueue({
    type: eventType,
    ...(data || {}),
  });
}

export function getSessionId() {
  return _getSessionId();
}

// ── スクロール深度トラッキング ────────────────────

const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const SHORT_PAGE_DELAY_MS = 1500;

function _emitScrollDepth(depth) {
  if (_scrollDepthsSent.has(depth)) return;
  _scrollDepthsSent.add(depth);
  _enqueue({
    type: 'scroll_depth',
    depth,
    page: _currentPage || window.location.pathname,
  });
}

function _windowMetrics() {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const docHeight = Math.max(document.body ? document.body.scrollHeight : 0, doc.scrollHeight) - window.innerHeight;
  return { scrollTop, docHeight };
}

// ウィンドウ以外（メインコンテンツが内部スクロールする要素）も対象にする。小さなカルーセル等は無視。
function _containerMetrics(el) {
  if (!el || el === document || el === document.documentElement || el === document.body) return null;
  if (!(el instanceof Element) || el.clientHeight < window.innerHeight * 0.4) return null;
  return { scrollTop: el.scrollTop || 0, docHeight: el.scrollHeight - el.clientHeight };
}

function _applyScrollMetrics({ scrollTop, docHeight }) {
  if (docHeight <= 0) return false;
  const pct = Math.round((scrollTop / docHeight) * 100);
  SCROLL_THRESHOLDS.forEach((t) => {
    if (pct >= t) _emitScrollDepth(t);
  });
  return true;
}

// 短いページ（スクロール不要）は一定時間後に depth 100 を1度だけ送る
function _scheduleShortPageCheck() {
  if (_shortPageTimer) clearTimeout(_shortPageTimer);
  _shortPageTimer = setTimeout(() => {
    _shortPageTimer = null;
    if (_windowMetrics().docHeight <= 0 && !document.querySelector('[data-scroll-container]')) {
      _emitScrollDepth(100);
    }
  }, SHORT_PAGE_DELAY_MS);
}

function _scheduleScrollEval() {
  if (!_initialized) return;
  if (_scrollEvalTimer) clearTimeout(_scrollEvalTimer);
  // 遷移直後はコンテンツ描画前なので少し待ってから評価する
  _scrollEvalTimer = setTimeout(() => {
    _scrollEvalTimer = null;
    _applyScrollMetrics(_windowMetrics());
  }, 500);
  _scheduleShortPageCheck();
}

function _trackScrollDepth() {
  let ticking = false;

  document.addEventListener(
    'scroll',
    (e) => {
      if (ticking) return;
      ticking = true;
      const target = e.target;
      requestAnimationFrame(() => {
        ticking = false;
        _applyScrollMetrics(_containerMetrics(target) || _windowMetrics());
      });
    },
    { passive: true, capture: true }
  );

  const reevaluate = () => {
    _applyScrollMetrics(_windowMetrics());
    _scheduleShortPageCheck();
  };
  window.addEventListener('resize', reevaluate);

  if (typeof ResizeObserver !== 'undefined' && document.body) {
    let lastHeight = document.body.scrollHeight;
    new ResizeObserver(() => {
      const h = document.body.scrollHeight;
      if (h === lastHeight) return;
      lastHeight = h;
      reevaluate();
    }).observe(document.body);
  }

  _scheduleShortPageCheck();
}

// ── クリックトラッキング（document 委譲） ────────────

const TRACK_ATTR_KEYS = ['rank', 'position', 'section', 'kind', 'placement'];
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label';

function _readTrackAttrs(el) {
  const meta = {};
  TRACK_ATTR_KEYS.forEach((key) => {
    const raw = el.getAttribute(`data-track-${key}`);
    if (raw == null || raw === '') return;
    const num = Number(raw);
    meta[key] = Number.isFinite(num) && /^\d+$/.test(raw) ? num : String(raw).slice(0, 60);
  });
  return meta;
}

function _navTarget(pathname) {
  if (/^\/procedures?\//.test(pathname)) return 'procedure_detail_nav';
  if (/^\/clinics?\//.test(pathname)) return 'clinic_detail_nav';
  if (/^\/login(\/|$)/.test(pathname) || /^\/api\/auth\//.test(pathname)) return 'login_entry';
  return null;
}

function _handleDocumentClick(e) {
  const origin = e.target instanceof Element ? e.target : null;
  if (!origin) return;

  const tracked = origin.closest('[data-track]');
  if (tracked) {
    const inner = origin.closest(INTERACTIVE_SELECTOR);
    // カード全体のトラッキングは、内部のボタン・リンク操作では発火させない
    if (!inner || inner === tracked || !tracked.contains(inner)) {
      const name = String(tracked.getAttribute('data-track') || '').slice(0, 60);
      if (name) trackClick(name, _readTrackAttrs(tracked));
    }
  }

  const anchor = origin.closest('a[href]');
  if (!anchor || anchor === tracked) return;
  let url;
  try {
    url = new URL(anchor.getAttribute('href'), window.location.href);
  } catch (_) {
    return;
  }
  if (!/^https?:$/.test(url.protocol)) return;

  if (url.host !== window.location.host) {
    // 外部リンクはホストのみ記録（パス・クエリは PII を含み得るため送らない）
    trackClick('outbound_link', { host: url.host.slice(0, 100) });
    return;
  }
  const target = _navTarget(url.pathname);
  if (target) {
    trackClick(target, { path: url.pathname.slice(0, 120) });
  }
}

function _trackClicks() {
  document.addEventListener('click', _handleDocumentClick, true);
}

